<script setup>
/**
 * One trip in full: what the airline says, what the customer asked for, what we sent them,
 * and how the trip got to where it is.
 *
 * This exists because two things the system already knew had nowhere to be read. The panel
 * said "check the segments before replying" while showing no segments, and the transition
 * log recorded every step of a trip's life with nothing rendering it.
 *
 * Opened from both the conversation sidebar and the contact page, which is why
 * acknowledgement lives here rather than only on the sidebar card - an agent looking at a
 * customer's record could see the alarm and not clear it.
 *
 * SEGMENT STATUS CODES ARE SHOWN RAW. `HK`, `UN` and `TK` are the airline's vocabulary and
 * we have no traced mapping from them to plain English; a friendly label here would be us
 * guessing what the carrier meant and an agent acting on the guess.
 *
 * NO PAYMENTS, for the same reason as the contact card: nothing links a Chatwoot invoice to
 * a Travelport booking, so a fare or a paid marker on this screen would be inventing a
 * relationship that does not exist.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  calendarDate,
  dateFormat,
  dynamicTime,
} from 'shared/helpers/timeHelper';
import { useTripActions } from 'dashboard/composables/useTripActions';
import TravelTripsAPI from 'dashboard/api/travel/trips';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  tripId: { type: [Number, String], required: true },
});

// The list that opened this keeps its own copy of the trip, and a refresh or an
// acknowledgement here has to reach it.
const emit = defineEmits(['close', 'updated']);

const { t } = useI18n();
const { refreshingId, acknowledgingId, refresh, acknowledge } =
  useTripActions();

const dialogRef = ref(null);
const trip = ref(null);
const isLoading = ref(false);

const requirements = computed(() => trip.value?.requirements || {});
const segments = computed(() => trip.value?.provider_segments || []);
const events = computed(() => trip.value?.events || []);
const quotes = computed(() => trip.value?.quotes || []);
const travellers = computed(() => trip.value?.travellers || []);

const route = computed(() => {
  const { origin, destination } = requirements.value;
  return origin && destination
    ? `${origin} → ${destination}`
    : t('TRAVEL.TRIP_DETAILS.NO_ROUTE');
});

const travelDates = computed(() =>
  [requirements.value.departure_date, requirements.value.return_date]
    .filter(Boolean)
    .map(date => calendarDate(date))
    .join(' — ')
);

const passengerCount = computed(() =>
  (requirements.value.passengers || []).reduce(
    (total, group) => total + Number(group.count || 0),
    0
  )
);

const statusLabel = status =>
  status ? t(`TRAVEL.TRIP_PANEL.STATUS.${status.toUpperCase()}`) : null;

// `{location, date, time}` as the retrieve mapper stores it - never reassembled into a
// timestamp, because the provider sends the three separately and joining them would assert
// a timezone nobody gave us.
const endpoint = point =>
  point
    ? [point.location, calendarDate(point.date), point.time]
        .filter(Boolean)
        .join(' · ')
    : '—';

const fetchTrip = async () => {
  isLoading.value = true;
  try {
    const { data } = await TravelTripsAPI.show(props.tripId);
    trip.value = data.trip;
  } catch (error) {
    trip.value = null;
  } finally {
    isLoading.value = false;
  }
};

const applyUpdate = updated => {
  if (!updated) return;
  // `show` carries the log, the shortlists and the travellers; `refresh` and `acknowledge`
  // answer with the trip alone. Merging keeps the sections below on screen instead of
  // emptying them on every click.
  trip.value = { ...trip.value, ...updated };
  emit('updated', updated);
};

const onRefresh = async () => applyUpdate(await refresh(trip.value));
const onAcknowledge = async () => applyUpdate(await acknowledge(trip.value));

// A log entry is read as "what happened when", so it names the hour. The travel dates
// above deliberately do not - a departure date has no hour attached.
const eventStamp = time => dateFormat(time, 'MMM d, yyyy · h:mm a');

onMounted(() => dialogRef.value?.open());

watch(() => props.tripId, fetchTrip, { immediate: true });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="t('TRAVEL.TRIP_DETAILS.CLOSE')"
    :title="route"
    :description="travelDates"
    @close="emit('close')"
  >
    <Spinner v-if="isLoading" class="mx-auto" />
    <p v-else-if="!trip" class="text-sm text-n-slate-11">
      {{ t('TRAVEL.TRIP_DETAILS.UNAVAILABLE') }}
    </p>

    <div v-else class="flex flex-col w-full gap-5">
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          {{ statusLabel(trip.status) }}
        </span>
        <span
          v-if="trip.record_locator"
          class="font-mono text-xs text-n-slate-11"
        >
          {{ trip.record_locator }}
        </span>
        <span
          v-if="trip.content_source"
          class="text-xs uppercase text-n-slate-10"
        >
          {{ trip.content_source }}
        </span>
        <span
          v-if="trip.hold?.label"
          class="px-2 py-0.5 text-xs rounded bg-n-amber-3 text-n-amber-11"
        >
          {{ trip.hold.label }}
        </span>
      </div>

      <div
        v-if="trip.schedule_changed"
        class="p-3 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2"
      >
        <p class="text-n-amber-11">
          {{ t('TRAVEL.TRIP_PANEL.SCHEDULE_CHANGED') }}
        </p>
        <Button
          xs
          faded
          slate
          class="mt-2"
          :is-loading="acknowledgingId === trip.id"
          :label="t('TRAVEL.TRIP_PANEL.ACKNOWLEDGE')"
          @click="onAcknowledge"
        />
      </div>

      <!-- What the airline says, and how old that answer is. The panel's alarm points here. -->
      <section v-if="trip.record_locator">
        <div class="flex items-center justify-between gap-2">
          <h5 class="text-sm font-medium text-n-slate-12">
            {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.TITLE') }}
          </h5>
          <Button
            xs
            ghost
            slate
            :is-loading="refreshingId === trip.id"
            :label="t('TRAVEL.TRIP_PANEL.REFRESH')"
            @click="onRefresh"
          />
        </div>
        <p
          class="mt-1 text-xs"
          :class="
            trip.provider_status_stale ? 'text-n-amber-11' : 'text-n-slate-10'
          "
        >
          {{
            trip.provider_checked_at
              ? t('TRAVEL.TRIP_PANEL.CHECKED', {
                  time: dynamicTime(trip.provider_checked_at),
                })
              : t('TRAVEL.TRIP_PANEL.NEVER_CHECKED')
          }}
        </p>

        <div v-if="segments.length" class="mt-2 overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs uppercase text-n-slate-10">
              <tr>
                <th class="py-1 pr-3 font-medium">
                  {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.FLIGHT') }}
                </th>
                <th class="py-1 pr-3 font-medium">
                  {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.FROM') }}
                </th>
                <th class="py-1 pr-3 font-medium">
                  {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.TO') }}
                </th>
                <th class="py-1 font-medium">
                  {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="segment in segments" :key="segment.sequence">
                <td
                  class="py-2 pr-3 font-medium whitespace-nowrap text-n-slate-12"
                >
                  {{ segment.carrier }}{{ segment.number }}
                </td>
                <td class="py-2 pr-3 text-n-slate-11">
                  {{ endpoint(segment.departure) }}
                </td>
                <td class="py-2 pr-3 text-n-slate-11">
                  {{ endpoint(segment.arrival) }}
                </td>
                <td class="py-2 font-mono text-xs text-n-slate-11">
                  {{ segment.status || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="mt-2 text-sm text-n-slate-11">
          {{ t('TRAVEL.TRIP_DETAILS.ITINERARY.EMPTY') }}
        </p>
      </section>

      <!-- The brief, as the agent captured it. `notes` is the customer's own words and
           arrives sanitized from the server (T7); Vue escapes it again on the way in. -->
      <section>
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.TRIP_DETAILS.BRIEF.TITLE') }}
        </h5>
        <dl
          class="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm sm:grid-cols-3"
        >
          <div v-if="passengerCount">
            <dt class="text-xs text-n-slate-10">
              {{ t('TRAVEL.TRIP_DETAILS.BRIEF.PASSENGERS') }}
            </dt>
            <dd class="text-n-slate-12">{{ passengerCount }}</dd>
          </div>
          <div v-if="requirements.cabin">
            <dt class="text-xs text-n-slate-10">
              {{ t('TRAVEL.TRIP_DETAILS.BRIEF.CABIN') }}
            </dt>
            <dd class="text-n-slate-12">{{ requirements.cabin }}</dd>
          </div>
          <div v-if="requirements.preferred_time">
            <dt class="text-xs text-n-slate-10">
              {{ t('TRAVEL.TRIP_DETAILS.BRIEF.PREFERRED_TIME') }}
            </dt>
            <dd class="capitalize text-n-slate-12">
              {{ requirements.preferred_time }}
            </dd>
          </div>
        </dl>
        <p v-if="requirements.notes" class="mt-2 text-sm text-n-slate-11">
          {{ requirements.notes }}
        </p>
      </section>

      <section v-if="travellers.length">
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.TRIP_DETAILS.TRAVELLERS.TITLE') }}
        </h5>
        <ul class="mt-2 text-sm divide-y divide-n-weak">
          <li
            v-for="traveller in travellers"
            :key="traveller.id"
            class="flex items-center gap-2 py-1.5"
          >
            <span class="text-n-slate-12">
              {{ traveller.given_name }} {{ traveller.surname }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ traveller.passenger_type_code }}
            </span>
          </li>
        </ul>
      </section>

      <!-- Every shortlist this trip was sold with, newest first. Superseded ones stay: a
           quote records what the customer was actually shown. -->
      <section v-if="quotes.length">
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.TRIP_DETAILS.QUOTES.TITLE') }}
        </h5>
        <div
          v-for="quote in quotes"
          :key="quote.id"
          class="pt-2 mt-2 border-t border-n-weak first:border-t-0 first:pt-0"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs text-n-slate-10">
              {{ eventStamp(quote.created_at) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                quote.selected_offer_key
                  ? t('TRAVEL.QUOTE.SELECTED', {
                      option: quote.selected_offer_key,
                    })
                  : t('TRAVEL.QUOTE.AWAITING')
              }}
            </span>
          </div>
          <ul class="mt-1 text-sm text-n-slate-11">
            <li v-for="option in quote.options" :key="option.key">
              {{ option.summary }}
            </li>
          </ul>
        </div>
      </section>

      <!-- Our lifecycle, oldest first. Not the airline's - that is the itinerary above. -->
      <section v-if="events.length">
        <h5 class="text-sm font-medium text-n-slate-12">
          {{ t('TRAVEL.TRIP_DETAILS.HISTORY.TITLE') }}
        </h5>
        <ol class="mt-2 text-sm divide-y divide-n-weak">
          <li
            v-for="event in events"
            :key="event.id"
            class="flex flex-wrap items-baseline gap-x-2 py-1.5"
          >
            <span class="text-n-slate-12">{{
              statusLabel(event.to_status)
            }}</span>
            <span v-if="event.from_status" class="text-xs text-n-slate-10">
              {{
                t('TRAVEL.TRIP_DETAILS.HISTORY.FROM', {
                  status: statusLabel(event.from_status),
                })
              }}
            </span>
            <span class="ml-auto text-xs text-n-slate-10">
              {{ eventStamp(event.occurred_at) }}
            </span>
          </li>
        </ol>
      </section>
    </div>
  </Dialog>
</template>
